(ns frontend.view.entity-merge.entity-info
  (:require
   [basic-components.field-editor :as field-editor]
   [clojure.set :as set]
   [clojure.string :as string]
   [common.attribute :as attribute]
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.data-types :as data-types]
   [common.schema.entity-type-names :as entity-type-names]
   [common.schema.field :as field]
   [common.schema.registry :as registry]
   [frontend.utils :as fu]
   [frontend.view.entity-merge.components :as components]
   [frontend.view.entity-merge.events :as events]
   [frontend.view.entity-viewers.modules.attachments :as attachments]
   [reagent.core :as r]
   [frontend.state.current-session :as session]
   [ui.components.button.view :as button]
   [ui.util :as uu]))

(defmethod components/step-info :entity-info
  [_signals [_step-kw _step-params]]
  (loc/tr :entity-merge-step/entity-info))

(defn multi-field?
  [tenant-schema field]
  ;; TODO handle and test text-multi, url-multi, and email-multi fields
  ;; (no such fields in mergable entities as of 2024-07-17)
  ;; (also see data-types/multi-data-type?)
  (let [{:field/keys [data-type] :as _field-schema} (registry/get-field-schema tenant-schema field)]
    (contains? #{:data.type/ref-multi :data.type/keyword-multi} data-type)))

(defn merge-value
  [{:keys [field rejection source target]}]
  (let [tenant-schema @session/tenant-schema
        target-id (:db/id target)]
    (cond
      (multi-field? tenant-schema field)
      (set/difference
       (set/union (get target field)
                  (get source field))
       rejection)

      (= rejection target-id)
      (get source field)

      :else
      (get target field))))

(defn visible-fields
  [available-fields source target]
  (let [tenant @session/tenant
        tenant-schema @session/tenant-schema
        non-empty-fields (->> [source target]
                              (mapcat keys)
                              distinct
                              (filter
                               (fn [k]
                                 (let [source-val (fu/get-value tenant tenant-schema source k)
                                       target-val (fu/get-value tenant tenant-schema target k)]
                                   (or (and (some? source-val)
                                            (not (:common/disabled? source-val)))
                                       (and (some? target-val)
                                            (not (:common/disabled? source-val)))))))
                              set)]
    (->> available-fields
         (filter non-empty-fields))))

(defn combined-info
  [{:keys [state r-target r-source] :as signals}]
  (let [target @r-target
        source @r-source
        rejection-path (fn [field]
                         (conj [:rejection :entity-info] field))
        available-fields (events/entity-fields signals)
        fields (visible-fields available-fields source target)
        entity-values (->> fields
                           (map
                            (fn [field]
                              [field
                               (merge-value {:field     field
                                             :source    source
                                             :target    target
                                             :rejection (get-in @state (rejection-path field))})]))
                           (into {}))]
    entity-values))

(defn resolve-editor-type
  [field-schema]
  (let [field-editor (->> field-schema
                          :field/default-visibility
                          :editor)]
    (if-not (= field-editor :editor.type/none)
      field-editor
      (->> field-schema
           :field/data-type
           (get data-types/zapflow-data-types)
           :default-editor))))

(defn review
  [{:keys [state entity-type] :as signals}]
  (let [result (combined-info signals)
        tenant-schema @session/tenant-schema
        available-fields (events/entity-fields signals)
        fields (->> available-fields
                    (filter (fn [field]
                              (contains? result field))))]
    [components/review-card signals
     {:title          (loc/tr :entity-type-info [(loc/tr entity-type)])
      :collapsed-path [:review-info]
      :content
      [:<>
       (doall
        (for [field fields
              :let [value (get result field)
                    field-schema (registry/get-field-schema tenant-schema field)
                    tag? (field/tag-field? field)
                    editor-type (if tag?
                                  :editor.type/multi-select
                                  (resolve-editor-type field-schema))]]
          ^{:key (str "f-" field)}
          [:div.mb-3
           (cond
             (= field :common/named-network-drives)
             [:div.input-editor
              [:div.input-editor__content
               [:div.input-editor__header
                [:div.input-editor__label
                 (loc/tr :common/named-network-drives)]]
               [attachments/network-drives-viewer
                {:entity result}]]]

             :else
             [field-editor/view
              (merge field-schema
                     {:state-atom    state
                      :editor/type   editor-type
                      :read-only?    true
                      :drag-options  {:hide-drag-area? true}
                      :draggable?    false
                      :nil-as-empty? true
                      :value         value})])]))]}]))

(defn select!
  [state path field]
  (swap! state update-in path (fnil conj #{}) field))

(defn unselect!
  [state path field]
  (swap! state update-in path (fnil disj #{}) field))

(defn use-field-editor?
  [{:keys [attribute]}]
  (contains? #{:company/financials
               :company/tesi-financials
               :company/tesi-financials-2
               ;;investor specific edn-field:
               :investor/allocation-split}
             attribute))

(defn single-field
  [{:keys [attribute tenant tenant-schema entity value field-schema left? selected? select!]}]
  [:div
   {:class (fu/cs (if left?
                         "col-span-4"
                         "col-span-4"))}
   [:div
    {:class (uu/cm "inline-flex w-full items-baseline py-2 px-3 break-words border border-solid border-base-light-border-paler rounded"
                        (when selected?
                          "border-base-light-border-accent text-base-light-text-accent"))
     :on-click   (fn [_]
                   (select!))}
    (if selected?
      [:i.fas.fa-check-circle.mr-2]
      [:i.fal.fa-circle.mr-2])
    (cond (use-field-editor? {:attribute attribute})
          [field-editor/view
           (merge
            field-schema
            {:read-only?  true
             :hide-label? true
             :class  "entity-merge-field"
             :value       (fu/get-value tenant tenant-schema entity attribute)
             :editor/type (resolve-editor-type field-schema)
             :entity      entity})]

          (= :common/named-network-drives attribute)
          [:div
           [attachments/network-drives-viewer
            {:entity entity}]]

          :else
          [:div
           {:title value}
           value])]])

(defn multi-field
  [{:keys [attribute field-schema values rejection left? toggle-value!]}]
  (let [{:keys [options]} field-schema]
    [:div
     {:class (uu/cm (if left?
                           "col-4"
                           "col-6"))}
     (doall
      (for [value values
            :let [react-key (or (:db/id value)
                                (str value))

                  display-value (cond
                                  (number? value)
                                  (-> value
                                      events/get-entity
                                      fu/resolve-name)

                                  (keyword? value)
                                  (or (fu/find-option-label options value)
                                      (name value))

                                  :else
                                  nil)
                  selected? (not (contains? rejection value))]]
        ^{:key (str attribute "-multifield-" react-key)}
        [:div
         {:class-name (uu/cm "inline-flex items-baseline py-2 px-3 break-words border border-solid border-base-light-border-paler rounded"
                             (when selected?
                               "border-base-light-border-accent text-base-light-text-accent"))
          :title      display-value
          :on-click   (partial toggle-value! value)}
         (if selected?
           [:i.fas.fa-check-circle.mr-2]
           [:i.fal.fa-circle.mr-2])
         display-value]))]))

(defn reject-single-fields
  [state-val fields reject-id]
  (->> fields
       (reduce (fn [s field]
                 (assoc-in s [:rejection :entity-info field] reject-id))
               state-val)))

(defn select-multi-fields
  [state-val fields entity]
  (->> fields
       (reduce (fn [s k]
                 (let [path [:rejection :entity-info k]
                       entity-val (set (get entity k))]
                   (update-in s path (fnil set/difference #{}) entity-val)))
               state-val)))

(defn preselect-target!
  [{:keys [state r-source r-target] :as signals}]
  (let [rejection-path [:rejection :entity-info]
        rejection (get-in @state rejection-path)
        no-selection? (nil? rejection)
        source @r-source
        target @r-target
        source-id (:db/id source)
        available-fields (events/entity-fields signals)
        fields (visible-fields available-fields source target)
        tenant-schema @session/tenant-schema
        single-fields (->> fields
                           (remove (partial multi-field? tenant-schema)))
        multi-fields (->> fields
                          (filter (partial multi-field? tenant-schema)))]
    (when no-selection?
      (swap! state
        (fn [s]
          (-> s
              (reject-single-fields single-fields source-id)
              (select-multi-fields multi-fields target)))))))

(defn select-all-button
  [select-all!]
  [button/secondary-open
   {:on-click   select-all!}
   (loc/tr :select-all)])

(defn select-all-left-button
  [{:keys [r-first-entity r-second-entity state] :as signals}]
  (let [right @r-second-entity
        left @r-first-entity
        right-id (:db/id right)
        left-id (:db/id left)

        available-fields (events/entity-fields signals)
        fields (visible-fields available-fields left right)
        tenant-schema @session/tenant-schema
        single-fields (->> fields
                           (remove (partial multi-field? tenant-schema)))
        multi-fields (->> fields
                          (filter (partial multi-field? tenant-schema)))
        select-all-left! (fn [_]
                           (swap! state
                             (fn [s]
                               (-> s
                                   (reject-single-fields single-fields right-id)
                                   (select-multi-fields multi-fields left)
                                   ;;timeline selection:
                                   (assoc :source-id right-id :target-id left-id)))))]
    [select-all-button select-all-left!]))

(defn select-all-right-button
  [{:keys [r-first-entity r-second-entity state] :as signals}]
  (let [right @r-second-entity
        left @r-first-entity
        left-id (:db/id left)
        right-id (:db/id right)

        available-fields (events/entity-fields signals)
        tenant-schema @session/tenant-schema
        fields (visible-fields available-fields left right)
        single-fields (->> fields
                           (remove (partial multi-field? tenant-schema)))
        multi-fields (->> fields
                          (filter (partial multi-field? tenant-schema)))
        select-all-right! (fn [_]
                            (swap! state
                              (fn [s]
                                (-> s
                                    (reject-single-fields single-fields left-id)
                                    (select-multi-fields multi-fields right)
                                    ;;timeline selection:
                                    (assoc :source-id left-id :target-id right-id)))))]
    [select-all-button select-all-right!]))

(defn select-all-row
  [signals]
  [:div
   [:div
    {:class "grid grid-cols-12 gap-4"}
    [:div
     {:class "col-span-2"}]
    [:div
     {:class "col-span-4 flex justify-center"}
     [select-all-left-button signals]]
    [:div
     {:class "col-span-6 flex justify-center"}
     [select-all-right-button signals]]]])

(defn timeline-selection
  [{:keys [state entity-type r-target-id r-first-entity-id r-second-entity-id]
    :as   _signals}]
  (let [first-id @r-first-entity-id
        target-id @r-target-id
        second-id @r-second-entity-id
        left-selected? (= target-id first-id)
        right-selected? (= target-id second-id)]
    [:div
     {:class "py-3"}
     [:div
      [:div
       {:class "grid grid-cols-12 gap-4 px-2"}
       [:div
        {:class "col-span-2 text-bold"}
        ;;{:title (loc/tr :timeline)}
        (loc/tr :timeline)
        [fu/tooltip-overlay
         {:id        "timeline-selection-tooltip"
          :placement "right"}
         (loc/tr :merge-timeline-tooltip
             [(-> entity-type
                  (entity-type-names/get-plural :en)
                  str
                  string/lower-case)])
         [:i.fas.fa-info-circle.ml-2]]]
       [:div
        {:class "col-span-4 flex justify-center"}
        [:div
         {:class (uu/cm "inline-flex items-baseline py-2 px-3 break-words border border-solid border-base-light-border-paler rounded"
                             (when left-selected?
                               "border-base-light-border-accent text-base-light-text-accent"))
          :on-click   (fn [_]
                        (swap! state assoc
                          :source-id second-id
                          :target-id first-id))}
         (if left-selected?
           [:i.fas.fa-check-circle]
           [:i.fal.fa-circle])]]
       [:div
        {:class "col-span-6 flex justify-center"}
        [:div
         {:class (uu/cm "inline-flex items-baseline py-2 px-3 break-words border border-solid border-base-light-border-paler rounded"
                             (when right-selected?
                               "border-base-light-border-accent text-base-light-text-accent"))
          :on-click   (fn [_]
                        (swap! state assoc
                          :source-id first-id
                          :target-id second-id))}
         (if right-selected?
           [:i.fas.fa-check-circle]
           [:i.fal.fa-circle])]]]]]))

(defn entities-title
  [{:keys [r-first-entity r-second-entity]}]
  (let [left @r-first-entity
        right @r-second-entity]
    [:div
     {:class "py-3"}
     [:div
      {:class "grid grid-cols-12 gap-4 px-2"}
      [:div
       {:class "col-span-2"}]
      [:div
       {:class "col-span-4 flex justify-center"}
       [:strong (entity/display-string left :long)]]
      [:div
       {:class "col-span-6 flex justify-center"}
       [:strong (entity/display-string right :long)]]]]))

(defn view
  [signals]
  (preselect-target! signals)
  (fn [{:keys [r-first-entity r-second-entity state] :as signals}]
    (let [right @r-second-entity
          tenant @session/tenant
          tenant-schema @session/tenant-schema
          left @r-first-entity
          right-id (:db/id right)
          left-id (:db/id left)

          available-fields (events/entity-fields signals)
          fields (visible-fields available-fields left right)
          ;;TODO all mandatory fields must be selected
          ;;TODO universal-email-address is not always required, even though it's :mandatory/always in schema.
          ;;     is this problem? are there places where it is required? (at least kpi-member? although it's no longer
          ;;     used after guest-account has been created)

          ;;note:
          ;;ref-multi & keyword-multi (cardinality/many): rejection contains explicit values
          ;;others (cardinality/one): rejection contains entity-id
          ;;note: if rejection is nil (i.e. user hasn't modified selections)
          ;;      consider all cardinality/one fields from source(/left/first-entity) as rejected
          ]
      [:div
       [entities-title signals]
       [select-all-row signals]
       [timeline-selection signals]
       (doall
        (for [k fields
              :let [field-schema (registry/get-field-schema tenant-schema k)
                    field-name (attribute/attribute-label tenant-schema k)
                    left-value (fu/display-data tenant-schema tenant k field-schema left)
                    right-value (fu/display-data tenant-schema tenant k field-schema right)
                    rejection-path [:rejection :entity-info k]
                    rejection (get-in @state rejection-path)]]
          ^{:key (str "merge-field-" k)}
          [:div
           {:class "py-3"}
           [:div
            [:div
             {:class "grid grid-cols-10 gap-4"}
             [:div
              {:class "col-span-2 text-bold"}
              ;;{:title field-name}
              field-name]
             (if (multi-field? tenant-schema k)
               [multi-field
                {:attribute     k
                 :field-schema  field-schema
                 :values        (get left k)
                 :rejection     rejection
                 :left?         true
                 :toggle-value! (r/partial events/toggle! state rejection-path)}]
               [single-field
                {:attribute     k
                 :tenant        tenant
                 :tenant-schema tenant-schema
                 :field-schema  field-schema
                 :selected?     (= rejection right-id)
                 :entity        left
                 :value         left-value
                 :left?         true
                 :select!       (fn []
                                  (swap! state assoc-in rejection-path right-id))}])
             (if (multi-field? tenant-schema k)
               [multi-field
                {:attribute     k
                 :field-schema  field-schema
                 :values        (get right k)
                 :rejection     rejection
                 :toggle-value! (r/partial events/toggle! state rejection-path)}]
               [single-field
                {:attribute     k
                 :tenant        tenant
                 :tenant-schema tenant-schema
                 :field-schema  field-schema
                 :entity        right
                 :selected?     (not= rejection right-id)
                 :value         right-value
                 :select!       (fn []
                                  (swap! state assoc-in rejection-path left-id))}])]]]))
       ;;TODO remove after testing:
       #_[review signals]])))
